import type { Case } from '@divorceos/schema';

export const API_BASE = '/api';

export interface CaseRecord {
  id: string;
  name: string | null;
  data: Case;
  caseNumber?: string;
  partyNames?: string[];
  createdAt?: string;
  updatedAt?: string;
  /** disOS versioning: the client this scenario belongs to, its label and position. */
  clientId?: string | null;
  label?: string;
  order?: number;
}

/** A client (folder/matter) with its scenario count; from GET /me/clients. */
export interface ClientRecord {
  id: string;
  name: string;
  caseNumber: string;
  partyNames: string[];
  createdAt?: string;
  updatedAt?: string;
  scenarioCount: number;
  lastEditedAt?: string;
  order?: number;
}

/** A client with its ordered scenarios; from GET /me/clients/:id. */
export interface ClientDetail extends ClientRecord {
  scenarios: CaseRecord[];
}

interface RawRecord {
  id: string | number;
  name?: string | null;
  data?: unknown;
  caseNumber?: string;
  partyNames?: unknown;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  clientId?: string | null;
  label?: string;
  order?: number;
}

interface RawClient {
  id: string | number;
  name?: string;
  caseNumber?: string;
  partyNames?: unknown;
  createdAt?: string;
  updatedAt?: string;
  scenarioCount?: number;
  lastEditedAt?: string;
  order?: number;
  scenarios?: RawRecord[];
}

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // credentials: 'include' sends the disOS session cookie with every API call.
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    ...init,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const body = (await res.json()) as { message?: string; error?: string }; msg = body.message ?? body.error ?? msg; } catch { /* keep default message */ }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** disOS auth: cookie-session accounts. */
export interface AuthUser { id: string; email: string }
export const authApi = {
  async me(): Promise<AuthUser | null> {
    try { return (await request<{ user: AuthUser }>('/auth/me')).user; }
    catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; }
  },
  async login(email: string, password: string): Promise<AuthUser> {
    return (await request<{ user: AuthUser }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) })).user;
  },
  async signup(email: string, password: string): Promise<AuthUser> {
    return (await request<{ user: AuthUser }>('/auth/signup', { method: 'POST', body: JSON.stringify({ email, password }) })).user;
  },
  async logout(): Promise<void> { await request<unknown>('/auth/logout', { method: 'POST' }); },
};

function normalizeRecord(raw: RawRecord): CaseRecord {
  return {
    id: String(raw.id),
    name: raw.name ?? null,
    data: (raw.data ?? {}) as Case,
    caseNumber: raw.caseNumber,
    partyNames: Array.isArray(raw.partyNames) ? raw.partyNames.map(String) : undefined,
    createdAt: raw.createdAt ?? raw.created_at,
    updatedAt: raw.updatedAt ?? raw.updated_at,
    clientId: raw.clientId ?? null,
    label: raw.label ?? '',
    order: typeof raw.order === 'number' ? raw.order : 0,
  };
}

function normalizeClient(raw: RawClient): ClientRecord {
  return {
    id: String(raw.id),
    name: raw.name ?? '',
    caseNumber: raw.caseNumber ?? '',
    partyNames: Array.isArray(raw.partyNames) ? raw.partyNames.map(String) : [],
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    scenarioCount: raw.scenarioCount ?? 0,
    lastEditedAt: raw.lastEditedAt,
    order: typeof raw.order === 'number' ? raw.order : 0,
  };
}

// disOS cases are scoped to the signed-in account (/me/cases), unlike the open /cases
// API the hybrid and binary apps use.
export const api = {
  async listCases(): Promise<CaseRecord[]> {
    const raw = await request<RawRecord[] | { cases: RawRecord[] }>('/me/cases');
    const arr = Array.isArray(raw) ? raw : raw.cases;
    return arr.map(normalizeRecord);
  },
  async getCase(id: string): Promise<CaseRecord> {
    return normalizeRecord(await request<RawRecord>(`/me/cases/${encodeURIComponent(id)}`));
  },
  async createCase(name: string | undefined, data: Case): Promise<CaseRecord> {
    return normalizeRecord(await request<RawRecord>('/me/cases', { method: 'POST', body: JSON.stringify({ name, data }) }));
  },
  async updateCase(id: string, name: string | undefined, data: Case): Promise<CaseRecord> {
    return normalizeRecord(await request<RawRecord>(`/me/cases/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ name, data }) }));
  },
  async deleteCase(id: string): Promise<void> {
    await request<unknown>(`/me/cases/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  /** Create a scenario: blank/with data, inside a client (clientId), and/or cloned from another scenario (cloneFrom). */
  async createScenario(body: { name?: string; data?: Case; clientId?: string; label?: string; cloneFrom?: string }): Promise<CaseRecord> {
    return normalizeRecord(await request<RawRecord>('/me/cases', { method: 'POST', body: JSON.stringify(body) }));
  },
  /** Rename or reorder a scenario (label/order only; case data is saved via updateCase). */
  async setScenarioMeta(id: string, meta: { label?: string; order?: number }): Promise<CaseRecord> {
    return normalizeRecord(await request<RawRecord>(`/me/cases/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(meta) }));
  },
  exportUrl(id: string, format: 'json' | 'xml'): string {
    return `${API_BASE}/me/cases/${encodeURIComponent(id)}/export.${format}`;
  },
};

/** disOS clients (the folder/matter grouping a case's scenarios). */
export const clientsApi = {
  async list(): Promise<ClientRecord[]> {
    const raw = await request<RawClient[]>('/me/clients');
    return raw.map(normalizeClient);
  },
  async get(id: string): Promise<ClientDetail> {
    const raw = await request<RawClient>(`/me/clients/${encodeURIComponent(id)}`);
    return { ...normalizeClient(raw), scenarios: (raw.scenarios ?? []).map(normalizeRecord) };
  },
  async update(id: string, patch: { name?: string; caseNumber?: string; order?: number }): Promise<ClientRecord> {
    return normalizeClient(await request<RawClient>(`/me/clients/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }));
  },
  async remove(id: string): Promise<void> {
    await request<unknown>(`/me/clients/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};
